import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { ProductService, CreateProductRequest } from '../types/productTypes';

export const useProductServices = () => {
  const [products, setProducts] = useState<ProductService[]>([]);
  const [loading, setLoading] = useState(false);

  // Simulate API calls - replace with actual API integration
  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      // Simulate loading from localStorage or API
      const savedProducts = localStorage.getItem('sonrie_products');
      if (savedProducts) {
        setProducts(JSON.parse(savedProducts));
      }
    } catch (error) {
      console.error('Error loading products:', error);
      message.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, []);

  const createProduct = async (productData: CreateProductRequest): Promise<ProductService> => {
    try {
      const newProduct: ProductService = {
        id: Date.now().toString(),
        ...productData,
        activo: true,
        fechaCreacion: new Date().toISOString()
      };

      const updatedProducts = [...products, newProduct];
      setProducts(updatedProducts);
      
      // Save to localStorage - replace with actual API call
      localStorage.setItem('sonrie_products', JSON.stringify(updatedProducts));
      
      message.success('Producto creado exitosamente');
      return newProduct;
    } catch (error) {
      console.error('Error creating product:', error);
      message.error('Error al crear producto');
      throw error;
    }
  };

  const updateProduct = async (id: string, productData: Partial<ProductService>): Promise<void> => {
    try {
      const updatedProducts = products.map(p => 
        p.id === id ? { ...p, ...productData } : p
      );
      setProducts(updatedProducts);
      localStorage.setItem('sonrie_products', JSON.stringify(updatedProducts));
      message.success('Producto actualizado exitosamente');
    } catch (error) {
      console.error('Error updating product:', error);
      message.error('Error al actualizar producto');
      throw error;
    }
  };

  const deleteProduct = async (id: string): Promise<void> => {
    try {
      const updatedProducts = products.filter(p => p.id !== id);
      setProducts(updatedProducts);
      localStorage.setItem('sonrie_products', JSON.stringify(updatedProducts));
      message.success('Producto eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting product:', error);
      message.error('Error al eliminar producto');
      throw error;
    }
  };

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return {
    products,
    loading,
    createProduct,
    updateProduct,
    deleteProduct,
    reloadProducts: loadProducts
  };
};
